import type { ApiErrorBody } from "@techplanner/shared/core";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly fields: Record<string, string> = {},
    readonly body?: unknown
  ) {
    super(message);
  }

  get isNetwork() {
    return this.status === 0;
  }
  get isConflict() {
    return this.status === 409;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Prévenu quand le serveur répond 401 (session expirée ou compte désactivé). */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

/** Requête interrompue : une annulation voulue (AbortError) remonte telle quelle ; le reste, délai dépassé compris, est une erreur réseau. */
function requestFailure(err: unknown): unknown {
  if ((err as Error).name === "AbortError") return err;
  return new ApiError(0, "Pas de connexion. Vérifiez le réseau puis réessayez.", "network");
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, idempotencyKey, signal } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  let payload: BodyInit | undefined;

  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`/api${path}`, { method, headers, body: payload, credentials: "same-origin", signal });
  } catch (err) {
    throw requestFailure(err);
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  let data: unknown = null;
  if (isJson) {
    try {
      data = await response.json();
    } catch (err) {
      // Corps interrompu par le signal (délai dépassé) : pas une réponse vide, une requête à refaire.
      if (signal?.aborted) throw requestFailure(err);
    }
  }

  if (!response.ok) {
    const err = (data ?? {}) as Partial<ApiErrorBody> & Record<string, unknown>;
    if (response.status === 401 && !path.startsWith("/auth/login")) {
      unauthorizedListeners.forEach((listener) => listener());
    }
    const fallback =
      response.status === 502 || response.status === 503 || response.status === 504
        ? "Serveur injoignable. Réessayez dans un instant."
        : response.status >= 500
          ? "Le serveur rencontre un problème. Réessayez dans un instant."
          : "La requête a échoué.";
    throw new ApiError(response.status, err.error ?? fallback, err.code, err.fields ?? {}, data);
  }

  return data as T;
}

/** Message lisible pour l'utilisateur, quelle que soit l'erreur. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Une erreur inattendue est survenue.";
}
